import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MagnifyingGlass, WarningCircle } from '@phosphor-icons/react';
import {
  API,
  authConfig,
  alertError,
  btnSecondary,
  fieldStyle,
  labelStyle,
  thStyle,
  tdStyle,
  pageTitleStyle,
  pageSubtitleStyle,
} from '../api/ticketsApi';
import { formatUtcToIst } from '../utils/datetime';
import TicketStatusBadge from '../components/TicketStatusBadge';
import TicketDomainBadge from '../components/TicketDomainBadge';
import ListSearch, { filterBySearch } from '../../../components/ListSearch';
import { TicketDetailLink } from '../ticketNav';
import { CategoryPie, SkeletonBlock, EmptyState } from '../../../components/module-ui';
import { useProductFilter } from '../ProductFilterContext';

const TICKET_SEARCH_KEYS = [
  'ticket_no', 'subject', 'update_reason', 'reason_for_resolution', 'assignee_names',
];

function statCard(label, value, color = '#002FA7') {
  return (
    <div className="tk-stat-card" style={{ borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: 700, color: '#111827', marginTop: '4px' }}>{value}</div>
    </div>
  );
}

function TicketRcaPage() {
  const { productId, selectedProduct } = useProductFilter();
  const [days, setDays] = useState(30);
  const [missingOnly, setMissingOnly] = useState(false);
  const [summary, setSummary] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadRca = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { days, missing_only: missingOnly };
      if (productId) params.product_id = productId;
      const [summaryRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/rca/summary`, authConfig(params)),
        axios.get(`${API}/rca/tickets`, authConfig({ ...params, page_size: 100 })),
      ]);
      setSummary(summaryRes.data);
      setTickets(ticketsRes.data?.items || []);
      setSearch('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load RCA metrics');
      setSummary(null);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRca();
  }, [days, missingOnly, productId]);

  const filteredTickets = useMemo(
    () => filterBySearch(tickets, search, TICKET_SEARCH_KEYS),
    [tickets, search],
  );

  const causeChart = useMemo(
    () => (summary?.top_causes || []).slice(0, 8).map((row) => ({
      label: row.label.length > 42 ? `${row.label.slice(0, 42)}…` : row.label,
      value: row.count,
    })),
    [summary],
  );

  const resolutionChart = useMemo(
    () => (summary?.top_resolutions || []).slice(0, 8).map((row) => ({
      label: row.label.length > 42 ? `${row.label.slice(0, 42)}…` : row.label,
      value: row.count,
    })),
    [summary],
  );

  return (
    <div>
      <div className="tk-page-head">
        <div className="tk-page-head-main">
          <h2 style={pageTitleStyle}>Root Cause Analysis</h2>
          <p style={pageSubtitleStyle}>
            Track reason for cause and resolution across completed tickets.
            {selectedProduct ? ` Scoped to ${selectedProduct.name}.` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <Link to="/dashboard/tickets/reports" className="tk-link" style={{ fontSize: '13px' }}>
            Ticket reports →
          </Link>
        </div>
      </div>

      {error && <div style={alertError}>{typeof error === 'string' ? error : JSON.stringify(error)}</div>}

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'flex-end' }}>
        <div>
          <label className="tk-label" style={labelStyle}>Period (days)</label>
          <select className="tk-field" style={fieldStyle} value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '8px' }}>
          <input
            type="checkbox"
            checked={missingOnly}
            onChange={(e) => setMissingOnly(e.target.checked)}
          />
          Show only tickets missing RCA
        </label>
        <button type="button" className="tk-btn tk-btn-secondary" style={btnSecondary} onClick={loadRca} disabled={loading}>
          Refresh
        </button>
      </div>

      {loading && !summary ? (
        <SkeletonBlock rows={6} />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            {statCard('Completed / closed', summary?.total ?? 0)}
            {statCard('With full RCA', summary?.with_rca ?? 0, '#059669')}
            {statCard('Missing RCA', summary?.missing_rca ?? 0, '#DC2626')}
            {statCard('Compliance', `${summary?.compliance_pct ?? 0}%`, '#002FA7')}
          </div>

          {(summary?.missing_rca > 0) && (
            <div style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
              padding: '12px 14px',
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: '10px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#991B1B',
            }}
            >
              <WarningCircle size={18} weight="fill" style={{ flexShrink: 0, marginTop: '1px' }} />
              <span>
                {summary.missing_rca} ticket(s) in this period are missing cause and/or resolution notes.
                Staff must fill both fields when marking tickets completed.
              </span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div className="tk-surface" style={{ padding: '16px' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '14px' }}>Top causes</h3>
              {causeChart.length ? (
                <CategoryPie data={causeChart} height={220} />
              ) : (
                <EmptyState title="No cause data" subtitle="Complete tickets with RCA to populate this chart." />
              )}
            </div>
            <div className="tk-surface" style={{ padding: '16px' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '14px' }}>Top resolutions</h3>
              {resolutionChart.length ? (
                <CategoryPie data={resolutionChart} height={220} />
              ) : (
                <EmptyState title="No resolution data" subtitle="Complete tickets with RCA to populate this chart." />
              )}
            </div>
          </div>

          <div className="tk-surface" style={{ padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '15px' }}>RCA ticket log</h3>
              <ListSearch
                value={search}
                onChange={setSearch}
                placeholder="Search ticket, cause, resolution…"
                icon={MagnifyingGlass}
              />
            </div>

            {filteredTickets.length === 0 ? (
              <EmptyState title="No tickets" subtitle="No completed tickets match the current filters." />
            ) : (
              <div className="tk-table-wrap">
                <table className="tk-table">
                  <thead>
                    <tr>
                      <th style={thStyle}>Ticket</th>
                      <th style={thStyle}>Subject</th>
                      <th style={thStyle}>Domain</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Reason for cause</th>
                      <th style={thStyle}>Reason for resolution</th>
                      <th style={thStyle}>Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map((t) => (
                      <tr key={t.ticket_no}>
                        <td style={tdStyle}>
                          <TicketDetailLink ticketNo={t.ticket_no} className="tk-link">
                            {t.ticket_no}
                          </TicketDetailLink>
                        </td>
                        <td style={tdStyle}>{t.subject}</td>
                        <td style={tdStyle}><TicketDomainBadge domain={t.domain} /></td>
                        <td style={tdStyle}><TicketStatusBadge status={t.status} /></td>
                        <td style={{ ...tdStyle, maxWidth: '220px', whiteSpace: 'pre-wrap' }}>
                          {t.update_reason || <span style={{ color: '#DC2626' }}>Missing</span>}
                        </td>
                        <td style={{ ...tdStyle, maxWidth: '220px', whiteSpace: 'pre-wrap' }}>
                          {t.reason_for_resolution || <span style={{ color: '#DC2626' }}>Missing</span>}
                        </td>
                        <td style={tdStyle}>{formatUtcToIst(t.completed_at || t.updated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default TicketRcaPage;
